/**
 * Data mapping utilities to transform API responses to form data format
 * Handles both snake_case and camelCase field names
 */

/**
 * Maps vendor register API response to LegalEntityDetailsPage form data
 * @param {Object} apiData - API response data
 * @returns {Object} Form data object
 */
export const mapVendorRegisterToForm = (apiData) => {
  if (!apiData) return null

  // Handle nested data structure (Vendor_Registration_Info)
  const data = apiData.Vendor_Registration_Info || apiData.vendor_registration_info || apiData

  // Extract country code from mobile number if present
  const extractCountryCode = (mobile) => {
    if (!mobile) return '+91'
    if (mobile.startsWith('+91')) return '+91'
    if (mobile.startsWith('91') && mobile.length > 10) return '+91'
    // Try to detect other country codes
    if (mobile.startsWith('+')) {
      const match = mobile.match(/^\+(\d{1,4})/)
      return match ? `+${match[1]}` : '+91'
    }
    return '+91'
  }

  // Extract mobile number without country code
  const extractMobileNumber = (mobile) => {
    if (!mobile) return ''
    // Remove country code if present
    let cleanMobile = mobile.replace(/^\+?\d{1,4}/, '')
    // Remove any non-digit characters
    cleanMobile = cleanMobile.replace(/\D/g, '')
    return cleanMobile
  }

  const contactMobile = data.contact_mobile || data.contactMobile || ''
  const countryCode = extractCountryCode(contactMobile)
  const mobileNumber = extractMobileNumber(contactMobile)

  // Check if operating address matches registered address
  const isSameAsRegistered = 
    (data.op_address_line1 || data.opAddressLine1 || '') === (data.reg_address_line1 || data.regAddressLine1 || '') &&
    (data.op_city || data.opCity || '') === (data.reg_city || data.regCity || '') &&
    (data.op_state || data.opState || '') === (data.reg_state || data.regState || '')

  return {
    legalEntityName: data.legal_entity_name || data.legalEntityName || '',
    cin: data.cin_llpin || data.cinLlpin || data.cin || '',
    gstin: data.gstin || '',
    pan: data.company_pan || data.companyPan || data.pan || '',
    registeredAddress: {
      addressLine1: data.reg_address_line1 || data.regAddressLine1 || '',
      addressLine2: data.reg_address_line2 || data.regAddressLine2 || '',
      city: data.reg_city || data.regCity || '',
      state: data.reg_state || data.regState || '',
      pincode: data.reg_pincode || data.regPincode || '',
      country: data.reg_country || data.regCountry || 'India'
    },
    operatingAddress: {
      addressLine1: data.op_address_line1 || data.opAddressLine1 || data.operating_address_line1 || '',
      addressLine2: data.op_address_line2 || data.opAddressLine2 || data.operating_address_line2 || '',
      city: data.op_city || data.opCity || data.operating_city || '',
      state: data.op_state || data.opState || data.operating_state || '',
      pincode: data.op_pincode || data.opPincode || data.operating_pincode || '',
      country: data.op_country || data.opCountry || data.operating_country || data.reg_country || data.regCountry || 'India'
    },
    contactPerson: {
      name: data.contact_name || data.contactName || '',
      designation: data.contact_designation || data.contactDesignation || '',
      email: data.contact_email || data.contactEmail || '',
      mobile: mobileNumber,
      countryCode: countryCode
    },
    preferredContactMethod: data.contact_method || data.contactMethod || 'email',
    isSameAsRegistered
  }
}

/**
 * Maps vendor profile API response to ProfileBuildingPage form data
 * @param {Object} apiData - API response data
 * @returns {Object} Form data object
 */
export const mapVendorProfileToForm = (apiData) => {
  if (!apiData) return null

  // Handle nested data structure (Vendor_Profile_Info)
  const data = apiData.Vendor_Profile_Info || apiData.vendor_profile_info || apiData

  // Parse other_social_handles if it's a string
  const parseSocialHandles = (handles) => {
    if (!handles) return []
    if (Array.isArray(handles)) return handles
    if (typeof handles === 'string') {
      try {
        // Try to parse as JSON first
        return JSON.parse(handles)
      } catch {
        // If not JSON, try to parse format like "Platform: URL | Platform: URL"
        const parts = handles.split('|').map(part => part.trim())
        return parts.map(part => {
          const [platform, url] = part.split(':').map(s => s.trim())
          return { platform: platform || '', url: url || '' }
        }).filter(h => h.platform && h.url)
      }
    }
    return []
  }

  return {
    companyDescription: {
      short: data.short_description || data.shortDescription || '',
      long: data.detailed_description || data.detailedDescription || data.long_description || ''
    },
    industryDomains: Array.isArray(data.industry_domains || data.industryDomains) 
      ? (data.industry_domains || data.industryDomains)
      : (typeof data.industry_domains === 'string' ? data.industry_domains.split(',').map(s => s.trim()) : []),
    keyTechnologies: Array.isArray(data.key_technologies || data.keyTechnologies)
      ? (data.key_technologies || data.keyTechnologies)
      : (typeof data.key_technologies === 'string' ? data.key_technologies.split(',').map(s => s.trim()) : []),
    yearsInOperation: data.years_in_operation || data.yearsInOperation || '',
    websiteUrl: data.website_url || data.websiteUrl || '',
    linkedinPage: data.linkedin_url || data.linkedinUrl || data.linkedin_page || '',
    otherSocialHandles: parseSocialHandles(data.other_social_handles || data.otherSocialHandles),
    // Logo and brand assets would need to be handled separately as they're files
    logo: data.logo_url || data.logoUrl ? {
      url: data.logo_url || data.logoUrl,
      name: 'logo',
      size: 0,
      type: 'image'
    } : null,
    brandAssets: data.brand_assets_url || data.brandAssetsUrl ? [{
      url: data.brand_assets_url || data.brandAssetsUrl,
      name: 'brand-asset',
      size: 0,
      type: 'image'
    }] : []
  }
}

